import { calibrationService } from "../../../services/CalibrationService";
import React, { useState, useCallback, useRef, useEffect } from "react";
import { Provider } from "react-redux";
import { useDispatch, useSelector } from "react-redux";
import { configureStore } from "@reduxjs/toolkit";
import { ClientsDropdown, DatetimeDropdown, ProjectsDropdown, DropdownWithAutocomplete } from "./DropdownV2";
import LoadingOverlay from "../../core/LoadingOverlay";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import mdUploadSlice, {
    loadDataset,
    selectCurrentClient,
    selectCurrentProject,
    selectCurrentVersionDttm,
    selectCurrentDataset,
    selectDatasetIsLoading,
    selectDatasetLoadingError,
    setClient,
    setProject,
    setVersionDttm,
    setDatasetType,
    setGroupKey,
} from "./mdUpload/store/reducers/mdUploadSlice";


type ReferenceRatingEntry = {
    cond_name: string;
    cond_type: string;
    mot: number;
    num_bundles: number;
    size: string;
    stranding: string;
    site_groups: number[];
};

const SUMMARY_FIELDS: { key: keyof ReferenceRatingEntry; label: string }[] = [
    { key: "cond_type", label: "Conductor Type" },
    { key: "cond_name", label: "Conductor Name" },
    { key: "mot", label: "MOT" },
    { key: "num_bundles", label: "Num of Bundles" },
    { key: "size", label: "Size" },
    { key: "stranding", label: "Stranding" },
];

// ---------- types & helpers ----------

const AAR_METHODOLOGY = "lookup";
const INTERPOLATION_OPTIONS = ["nearest", "linear", "lowest", "highest"];

type AarTableRow = { key: string; value: string };

type AarConfig = {
    aarTable: AarTableRow[];
    interpolation: string;
};

type RatingConfigState =
    | { mode: "all_day"; config: AarConfig }
    | { mode: "day_night"; dayConfig: AarConfig; nightConfig: AarConfig };

const DEFAULT_AAR_CONFIG: AarConfig = { aarTable: [], interpolation: "lowest" };
const DEFAULT_ALL_DAY: RatingConfigState = { mode: "all_day", config: { ...DEFAULT_AAR_CONFIG } };

function deserializeAarConfig(rawConfig: any): AarConfig {
    return {
        aarTable: Object.entries(rawConfig?.AAR_table ?? {}).map(([k, v]) => ({ key: k, value: String(v) })),
        interpolation: rawConfig?.AAR_lookup_interpolation ?? "lowest",
    };
}

function deserializeRatingConfig(existingRating: any): RatingConfigState {
    const cfg = existingRating?.clientRatingConfiguration ?? existingRating;
    if (cfg && "AAR_configs" in cfg) {
        return {
            mode: "day_night",
            dayConfig: deserializeAarConfig(cfg.AAR_configs?.day),
            nightConfig: deserializeAarConfig(cfg.AAR_configs?.night),
        };
    }
    return { mode: "all_day", config: deserializeAarConfig(cfg) };
}

function serializeAarConfig(config: AarConfig): object {
    const table: Record<string, number> = {};
    for (const row of config.aarTable) {
        const k = row.key.trim();
        const v = parseFloat(row.value);
        if (k && !isNaN(v)) table[k] = v;
    }
    return { AAR_table: table, AAR_methodology: AAR_METHODOLOGY, AAR_lookup_interpolation: config.interpolation };
}

function serializeRatingConfig(state: RatingConfigState): object {
    if (state.mode === "all_day") return serializeAarConfig(state.config);
    return {
        AAR_configs: {
            day: serializeAarConfig(state.dayConfig),
            night: serializeAarConfig(state.nightConfig),
        },
        AAR_methodology: "day_night",
    };
}

function isValidNumber(s: string): boolean {
    const t = s.trim();
    return t !== "" && !isNaN(parseFloat(t)) && isFinite(Number(t));
}

function validateAarConfig(config: AarConfig): boolean {
    return config.aarTable.every((row) => {
        const hasKey = row.key.trim() !== "";
        const hasVal = row.value.trim() !== "";
        if (!hasKey && !hasVal) return true;
        return isValidNumber(row.key) && isValidNumber(row.value);
    });
}

function validateRatingConfig(state: RatingConfigState): boolean {
    if (state.mode === "all_day") return validateAarConfig(state.config);
    return validateAarConfig(state.dayConfig) && validateAarConfig(state.nightConfig);
}

function ratingConfigEqualsExisting(state: RatingConfigState, existingRating: any): boolean {
    return JSON.stringify(serializeRatingConfig(state)) ===
        JSON.stringify(serializeRatingConfig(deserializeRatingConfig(existingRating)));
}

// ---------- sub-components ----------

function AarTableEditor({ rows, onChange }: { rows: AarTableRow[]; onChange: (rows: AarTableRow[]) => void }) {
    return (
        <div>
            <div style={{ display: "grid", gridTemplateColumns: "90px 90px auto", gap: 3, marginBottom: 2 }}>
                <span style={{ fontSize: "0.7rem", fontWeight: 600 }}>Temp (°C)</span>
                <span style={{ fontSize: "0.7rem", fontWeight: 600 }}>Rating (A)</span>
                <span />
            </div>
            {rows.map((row, i) => {
                const keyFilled = row.key.trim() !== "";
                const valFilled = row.value.trim() !== "";
                const keyError = (keyFilled && !isValidNumber(row.key)) || (!keyFilled && valFilled);
                const valError = (valFilled && !isValidNumber(row.value)) || (keyFilled && !valFilled);
                return (
                    <div key={i} style={{ display: "grid", gridTemplateColumns: "90px 90px auto", gap: 3, marginBottom: 3 }}>
                        <TextField
                            size="small"
                            value={row.key}
                            error={keyError}
                            helperText={keyError ? (keyFilled ? "Must be a number" : "Required") : undefined}
                            onChange={(e) => {
                                const updated = [...rows];
                                updated[i] = { ...updated[i], key: e.target.value };
                                onChange(updated);
                            }}
                            placeholder="0"
                            inputProps={{ style: { fontSize: "0.78rem", padding: "4px 8px" } }}
                        />
                        <TextField
                            size="small"
                            type="number"
                            value={row.value}
                            error={valError}
                            helperText={valError ? (valFilled ? "Must be a number" : "Required") : undefined}
                            onChange={(e) => {
                                const updated = [...rows];
                                updated[i] = { ...updated[i], value: e.target.value };
                                onChange(updated);
                            }}
                            placeholder="100"
                            inputProps={{ style: { fontSize: "0.78rem", padding: "4px 8px" } }}
                        />
                        <Button
                            size="small"
                            color="error"
                            style={{ minWidth: 0, fontSize: "0.7rem", padding: "2px 6px" }}
                            onClick={() => onChange(rows.filter((_, idx) => idx !== i))}
                        >
                            ✕
                        </Button>
                    </div>
                );
            })}
            <Button
                size="small"
                variant="outlined"
                onClick={() => onChange([...rows, { key: "", value: "" }])}
                style={{ marginTop: 4, fontSize: "0.7rem" }}
            >
                + Add Row
            </Button>
        </div>
    );
}

function AarConfigSection({
    label,
    config,
    onChange,
}: {
    label?: string;
    config: AarConfig;
    onChange: (c: AarConfig) => void;
}) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {label && <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{label}</div>}
            <AarTableEditor rows={config.aarTable} onChange={(aarTable) => onChange({ ...config, aarTable })} />
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.55)" }}>Interpolation:</span>
                <TextField
                    select
                    variant="standard"
                    size="small"
                    value={config.interpolation}
                    onChange={(e) => onChange({ ...config, interpolation: e.target.value })}
                    InputProps={{ style: { fontSize: "0.75rem" } }}
                    style={{ minWidth: 90 }}
                >
                    {INTERPOLATION_OPTIONS.map((o) => (
                        <MenuItem key={o} value={o} style={{ fontSize: "0.75rem" }}>
                            {o}
                        </MenuItem>
                    ))}
                </TextField>
            </div>
        </div>
    );
}

// ---------- main row component ----------

type ExistingCheckResult = { isExisting: boolean; existingRating: any } | null;

function AarTableReadOnly({ config, label }: { config: AarConfig; label?: string }) {
    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {label && <div style={{ fontWeight: 600, fontSize: "0.9rem" }}>{label}</div>}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 4, maxWidth: 280 }}>
                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "rgba(0,0,0,0.55)" }}>Temp (°C)</span>
                <span style={{ fontSize: "0.75rem", fontWeight: 600, color: "rgba(0,0,0,0.55)" }}>Rating (A)</span>
                {config.aarTable.map((r) => (
                    <React.Fragment key={r.key}>
                        <span style={{ fontSize: "0.82rem" }}>{r.key}</span>
                        <span style={{ fontSize: "0.82rem" }}>{r.value}</span>
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
}

function ReferenceRatingRow({
    row,
    clientName,
    existingCheck,
    onRefreshExistingCheck,
    onSaveResult,
}: {
    row: ReferenceRatingEntry;
    clientName: string;
    existingCheck: ExistingCheckResult;
    onRefreshExistingCheck: () => void;
    onSaveResult: (text: string, isPartial: boolean, isError: boolean) => void;
}) {
    const [ratingConfig, setRatingConfig] = useState<RatingConfigState>(DEFAULT_ALL_DAY);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);

    useEffect(() => {
        if (existingCheck?.isExisting && existingCheck.existingRating) {
            setRatingConfig(deserializeRatingConfig(existingCheck.existingRating));
            setIsEditing(false);
        }
    }, [existingCheck]);

    const handleModeChange = useCallback(
        (_: React.MouseEvent, value: "all_day" | "day_night") => {
            if (!value) return;
            setRatingConfig((prev) => {
                if (value === "all_day") {
                    const config = prev.mode === "day_night" ? prev.dayConfig : prev.config;
                    return { mode: "all_day", config };
                } else {
                    const base = prev.mode === "all_day" ? prev.config : prev.dayConfig;
                    const nightConfig = prev.mode === "day_night" ? prev.nightConfig : { ...base };
                    return { mode: "day_night", dayConfig: base, nightConfig };
                }
            });
        },
        []
    );

    const isExisting = existingCheck?.isExisting ?? false;
    const existingRating = existingCheck?.existingRating ?? null;
    const isValid = validateRatingConfig(ratingConfig);
    const isUnchanged = isExisting && existingRating != null && ratingConfigEqualsExisting(ratingConfig, existingRating);
    const canSave = isValid && !isUnchanged;

    const handleSave = useCallback(async () => {
        const parsedConfig = serializeRatingConfig(ratingConfig);
        setIsSaving(true);
        try {
            let result: any;
            if (isExisting) {
                result = await calibrationService.updateExistingClientRatingConfig({
                    data: { existing_client_rating: existingRating, new_client_rating_config: parsedConfig },
                });
            } else {
                result = await calibrationService.UploadClientRatingSiteGroups(clientName, {
                    data: {
                        cond_type: row.cond_type,
                        num_bundles: row.num_bundles,
                        stranding: row.stranding,
                        mot: row.mot,
                        size: row.size,
                        rating_config: parsedConfig,
                        site_groups: row.site_groups,
                    },
                });
            }
            const isPartial = result?.status === 207;
            const isError = result?.status !== 200 && result?.status !== 207;
            onSaveResult(result?.message ?? "", isPartial, isError);
            if (!isError) onRefreshExistingCheck();
        } catch (error) {
            onSaveResult(`${error}`, false, true);
        } finally {
            setIsSaving(false);
        }
    }, [ratingConfig, isExisting, existingRating, row, clientName, onRefreshExistingCheck, onSaveResult]);

    return (
        <Accordion disableGutters>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <div style={{ position: "relative", width: "100%" }}>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${SUMMARY_FIELDS.length}, 1fr)`,
                            width: "100%",
                        }}
                    >
                        {SUMMARY_FIELDS.map((col) => (
                            <span key={col.key}>{row[col.key]}</span>
                        ))}
                    </div>
                    {existingCheck !== null && (
                        <span
                            style={{
                                position: "absolute",
                                right: 0,
                                top: "50%",
                                transform: "translateY(-50%)",
                                fontSize: "0.72rem",
                                fontWeight: 600,
                                color: isExisting ? "#2e7d32" : "#c62828",
                                background: isExisting ? "#e8f5e9" : "#ffebee",
                                borderRadius: 4,
                                padding: "2px 8px",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {isExisting ? "Configured" : "Not Configured"}
                        </span>
                    )}
                </div>
            </AccordionSummary>
            <AccordionDetails style={{ padding: "16px 16px 0", display: "flex", flexDirection: "column", gap: 16 }}>
                {existingCheck === null ? (
                    <CircularProgress size={24} />
                ) : isExisting && !isEditing ? (
                    <div style={{ display: "flex", gap: 24, alignItems: "flex-start", paddingBottom: 16 }}>
                        {ratingConfig.mode === "all_day" ? (
                            <AarTableReadOnly config={ratingConfig.config} />
                        ) : (
                            <div style={{ display: "flex", gap: 32 }}>
                                <AarTableReadOnly label="Day" config={ratingConfig.dayConfig} />
                                <Divider orientation="vertical" flexItem />
                                <AarTableReadOnly label="Night" config={ratingConfig.nightConfig} />
                            </div>
                        )}
                        <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-start" }}>
                            <span style={{ fontSize: "0.75rem", color: "rgba(0,0,0,0.55)" }}>
                                Interpolation:{" "}
                                <strong>
                                    {ratingConfig.mode === "all_day"
                                        ? ratingConfig.config.interpolation
                                        : ratingConfig.dayConfig.interpolation}
                                </strong>
                            </span>
                            <Button variant="outlined" size="small" onClick={() => setIsEditing(true)}>
                                Edit
                            </Button>
                        </div>
                    </div>
                ) : (
                    <>
                        <ToggleButtonGroup
                            value={ratingConfig.mode}
                            exclusive
                            onChange={handleModeChange}
                            size="small"
                        >
                            <ToggleButton value="all_day">All Day Ratings</ToggleButton>
                            <ToggleButton value="day_night">Day and Night Ratings</ToggleButton>
                        </ToggleButtonGroup>

                        {ratingConfig.mode === "all_day" ? (
                            <AarConfigSection
                                config={ratingConfig.config}
                                onChange={(config) => setRatingConfig({ mode: "all_day", config })}
                            />
                        ) : (
                            <div style={{ display: "flex", gap: 16 }}>
                                <div style={{ flex: 1 }}>
                                    <AarConfigSection
                                        label="Day"
                                        config={ratingConfig.dayConfig}
                                        onChange={(dayConfig) =>
                                            setRatingConfig({ ...ratingConfig, mode: "day_night", dayConfig })
                                        }
                                    />
                                </div>
                                <Divider orientation="vertical" flexItem />
                                <div style={{ flex: 1 }}>
                                    <AarConfigSection
                                        label="Night"
                                        config={ratingConfig.nightConfig}
                                        onChange={(nightConfig) =>
                                            setRatingConfig({ ...ratingConfig, mode: "day_night", nightConfig })
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        <div style={{ display: "flex", alignItems: "center", gap: 12, paddingBottom: 16 }}>
                            {isExisting && (
                                <Button variant="outlined" size="small" onClick={() => setIsEditing(false)}>
                                    Cancel
                                </Button>
                            )}
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                disabled={isSaving || !canSave}
                                startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {isExisting ? "Update" : "Save"}
                            </Button>
                        </div>
                    </>
                )}
            </AccordionDetails>
        </Accordion>
    );
}

function ReferenceRatingTable({ rows, clientName, onSaveResult }: { rows: ReferenceRatingEntry[]; clientName: string; onSaveResult: (text: string, isPartial: boolean, isError: boolean) => void }) {
    const [existingChecks, setExistingChecks] = useState<(ExistingCheckResult | null)[]>(() =>
        rows.map(() => null)
    );

    const checkRow = useCallback((row: ReferenceRatingEntry, index: number) => {
        setExistingChecks((prev) => {
            const updated = [...prev];
            updated[index] = null;
            return updated;
        });
        console.log("CheckifExistingClientRating payload:", { data: row });
        calibrationService
            .checkifExistingClientRating({ data: row })
            .then((result: any) => ({
                isExisting: result?.all_exist ?? false,
                existingRating: result?.data?.existing_client_rating ?? null,
            }))
            .catch(() => ({ isExisting: false, existingRating: null }))
            .then((check) =>
                setExistingChecks((prev) => {
                    const updated = [...prev];
                    updated[index] = check;
                    return updated;
                })
            );
    }, []);

    useEffect(() => {
        setExistingChecks(rows.map(() => null));
        rows.forEach((row, i) => checkRow(row, i));
    }, [rows, checkRow]);

    return (
        <div style={{ width: "100%", marginTop: 16 }}>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${SUMMARY_FIELDS.length}, 1fr) 40px`,
                    padding: "8px 48px 8px 16px",
                    fontWeight: "bold",
                    borderBottom: "1px solid rgba(0,0,0,0.12)",
                }}
            >
                {SUMMARY_FIELDS.map((col) => (
                    <span key={col.key}>{col.label}</span>
                ))}
            </div>
            {rows.map((row, i) => (
                <ReferenceRatingRow
                    key={i}
                    row={row}
                    clientName={clientName}
                    existingCheck={existingChecks[i]}
                    onRefreshExistingCheck={() => checkRow(row, i)}
                    onSaveResult={onSaveResult}
                />
            ))}
        </div>
    );
}

const RATING_TYPE_OPTIONS = [
    { value: "AAR" },
    { value: "Seasonal Static" },
    { value: "Static" },
];
const MASTERDATA_RECORDS_UPLOAD_TYPE = "masterdata-records-upload" as const;
const GROUP_KEY = "default" as const;

const referenceRatingStore = configureStore({
    reducer: { mdUpload: mdUploadSlice.reducer },
});

type ReferenceRatingState = ReturnType<typeof referenceRatingStore.getState>;
type ReferenceRatingDispatch = typeof referenceRatingStore.dispatch;

function ReferenceRatingUploadInner() {
    const dispatch = useDispatch<ReferenceRatingDispatch>();
    const clientName = useSelector((state: ReferenceRatingState) => selectCurrentClient(state));
    const projectName = useSelector((state: ReferenceRatingState) => selectCurrentProject(state));
    const versionDttm = useSelector((state: ReferenceRatingState) => selectCurrentVersionDttm(state));
    const dataset = useSelector((state: ReferenceRatingState) => selectCurrentDataset(state));
    const isLoading = useSelector((state: ReferenceRatingState) => selectDatasetIsLoading(state));
    const datasetLoadingError = useSelector((state: ReferenceRatingState) => selectDatasetLoadingError(state));
    const [ratingType, setRatingType] = useState<string>("");
    const [aarResponse, setAarResponse] = useState<{ data: ReferenceRatingEntry[] } | null>(null);
    const [aarIsLoading, setAarIsLoading] = useState<boolean>(false);
    const [dropdownError, setDropdownError] = useState<string>("");
    const [saveResult, setSaveResult] = useState<{ text: string; isPartial: boolean; isError: boolean } | null>(null);

    const handleSaveResult = useCallback((text: string, isPartial: boolean, isError: boolean) => {
        setSaveResult({ text, isPartial, isError });
    }, []);
    const errorTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const setErrorWithDefaultTimeout = useCallback((error: string) => {
        setDropdownError(error);
        if (errorTimeoutRef.current) {
            clearTimeout(errorTimeoutRef.current);
        }
        errorTimeoutRef.current = setTimeout(() => setDropdownError(""), 10000);
    }, []);

    useEffect(() => {
        dispatch(setDatasetType(MASTERDATA_RECORDS_UPLOAD_TYPE));
        dispatch(setGroupKey(GROUP_KEY));
    }, [dispatch]);

    useEffect(() => {
        if (versionDttm) {
            dispatch(loadDataset());
        }
    }, [versionDttm, dispatch]);

    useEffect(() => {
        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, []);

    const resetAarState = useCallback(() => {
        setAarResponse(null);
        setRatingType("");
    }, []);

    const handleClientChange = useCallback(
        (value: string) => {
            dispatch(setClient(value));
            resetAarState();
        },
        [dispatch, resetAarState]
    );

    const handleProjectChange = useCallback(
        (value: string) => {
            dispatch(setProject(value));
            resetAarState();
        },
        [dispatch, resetAarState]
    );

    const handleVersionDttmChange = useCallback(
        (value: string) => {
            dispatch(setVersionDttm(value));
            resetAarState();
        },
        [dispatch, resetAarState]
    );

    const handleAarRatingType = useCallback(async () => {
        if (!dataset) return;
        try {
            setAarIsLoading(true);
            const response = await calibrationService.pullReferenceRatingMapping(dataset);
            setAarResponse(response);
        } catch (error) {
            setErrorWithDefaultTimeout(`Failed to load AAR ratings: ${error}`);
        } finally {
            setAarIsLoading(false);
        }
    }, [dataset, setErrorWithDefaultTimeout]);

    const errorMessage = dropdownError || datasetLoadingError;

    return (
        <div
            style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                width: "90%",
                margin: "0 auto",
                padding: "16px",
            }}
        >
            <h1 style={{ textAlign: "center" }}>Reference Rating Upload</h1>
            <LoadingOverlay isLoading={isLoading} message="Loading dataset..." />
            <form
                style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "16px",
                    width: "100%",
                    marginBottom: 16,
                }}
            >
                <div style={{ display: "flex", gap: "16px", width: "100%" }}>
                    <ClientsDropdown
                        value={clientName}
                        setValue={handleClientChange}
                        setError={setErrorWithDefaultTimeout}
                        styleClass={{ flex: 2, mx: 1 }}
                    />
                    <ProjectsDropdown
                        clientName={clientName}
                        value={projectName}
                        setValue={handleProjectChange}
                        setError={setErrorWithDefaultTimeout}
                        styleClass={{ flex: 2, mx: 1 }}
                    />
                    <DatetimeDropdown
                        clientName={clientName}
                        projectName={projectName}
                        datasetType={MASTERDATA_RECORDS_UPLOAD_TYPE}
                        group={GROUP_KEY}
                        value={versionDttm}
                        setValue={handleVersionDttmChange}
                        setError={setErrorWithDefaultTimeout}
                        name="MD Upload Datetime"
                        styleClass={{ flex: 1, mx: 1 }}
                    />
                </div>
                <div style={{ display: "flex", gap: "16px", width: "100%" }}>
                    <DropdownWithAutocomplete
                        label="Rating Type"
                        value={ratingType}
                        setValue={(value: string) => {
                            setRatingType(value);
                            if (value === "Static" || value === "Seasonal Static") {
                                setErrorWithDefaultTimeout(`${value} Rating Type is not yet supported`);
                            } else if (value === "AAR") {
                                handleAarRatingType();
                            }
                        }}
                        options={RATING_TYPE_OPTIONS}
                        refreshOptions={() => {}}
                        refreshDisabled={true}
                        isLoading={false}
                        disabled={false}
                        styleClass={{ flex: 1, mx: 1 }}
                    />
                </div>
                {saveResult && (
                    <p style={{ marginTop: "8px", fontWeight: "bold", fontSize: "16px", color: saveResult.isError ? "red" : saveResult.isPartial ? "orange" : "green" }}>
                        {typeof saveResult.text === "object" ? JSON.stringify(saveResult.text) : saveResult.text}
                    </p>
                )}
            </form>
            {errorMessage && (
                <p
                    style={{
                        marginTop: "8px",
                        fontWeight: "bold",
                        fontSize: "20px",
                        color: "red",
                    }}
                >
                    {errorMessage}
                </p>
            )}
            <LoadingOverlay isLoading={aarIsLoading} message="Loading AAR combinations..." />
            {aarResponse?.data && <ReferenceRatingTable rows={aarResponse.data} clientName={clientName} onSaveResult={handleSaveResult} />}
        </div>
    );
}

export default function ReferenceRatingUpload() {
    return (
        <Provider store={referenceRatingStore}>
            <ReferenceRatingUploadInner />
        </Provider>
    );
}
